import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Middle band per PRD §5.2. Deliberately thin (~10 % of viewport).
 *
 *   • Caption above the input: ASK ANYTHING ON DI DECK (small-caps,
 *     centered, sovereign-purple).
 *   • Input: single centered line, ~60 % of viewport width, placeholder
 *     "Tell us what you are doing, and we will tell you what DI Deck
 *     can do for you."
 *   • Submit → /ask?q=<query>. The /ask page + the RAG back-end arrive
 *     in Phase 2 per PRD §11.2. For now the route is a stub.
 *   • Three suggested question chips below the input.
 */
export function AskAnything(): JSX.Element {
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    const trimmed = q.trim();
    if (!trimmed) return;
    navigate(`/ask?q=${encodeURIComponent(trimmed)}`);
  }

  return (
    <section
      aria-label="Ask anything on DI Deck"
      className="flex flex-col items-center justify-center bg-gradient-to-b from-brand-purple-light/10 via-sovereign-900 to-sovereign-900 px-4 py-4 md:py-5"
    >
      <h2 className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-purple md:text-xs">
        Ask anything on DI Deck
      </h2>
      <form
        onSubmit={onSubmit}
        className="flex w-full max-w-2xl items-center gap-2 rounded-full border border-sovereign-line bg-white/95 px-4 py-2 shadow-card focus-within:border-brand-purple focus-within:shadow-card-hover"
      >
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Tell us what you are doing, and we will tell you what DI Deck can do for you"
          aria-label="Your question"
          className="flex-1 bg-transparent text-sm italic text-ink placeholder:text-ink-dim focus:outline-none md:text-[15px]"
        />
        <button
          type="submit"
          aria-label="Ask"
          disabled={!q.trim()}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-purple text-white transition-colors hover:bg-brand-purple-dark disabled:opacity-40"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </form>
      <div className="mt-3 hidden flex-wrap items-center justify-center gap-2 md:flex">
        <SuggestionChip onClick={() => { setQ('How does DI Deck comply with the EU AI Act?'); }}>
          How does DI Deck comply with the EU AI Act?
        </SuggestionChip>
        <SuggestionChip onClick={() => { setQ('How do you handle regulated data?'); }}>
          How do you handle regulated data?
        </SuggestionChip>
        <SuggestionChip onClick={() => { setQ('What does the pricing look like?'); }}>
          What does the pricing look like?
        </SuggestionChip>
      </div>
    </section>
  );
}

function SuggestionChip({
  onClick, children,
}: {
  onClick: () => void;
  children: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border border-sovereign-line bg-white/60 px-3 py-1 text-[11px] font-medium text-ink-mute transition-colors hover:border-brand-purple hover:text-brand-purple"
    >
      {children}
    </button>
  );
}
